import { useEffect } from "react";

const SITE = "https://drshantanugupta.com";
const DEFAULT_DESCRIPTION =
  "Dr. Shantanu Kumar Gupta — MBBS, MS (MAMC), DNB, FICO (Merit). Cataract, cornea & medical retina specialist at Shantanu Netralaya Eye Hospital, Varanasi.";

/**
 * Injects (or replaces) a page-specific JSON-LD script tag.
 * The tag is a single, stable #page-json-ld element that any route can update.
 * Deps are keyed on the serialized JSON so re-renders that produce the same
 * schema payload don't thrash the DOM (which broke tag detection in some tests).
 */
/**
 * Injects a page-specific JSON-LD script tag.
 *
 * React 19 auto-manages <head> and can drop imperatively-appended <script>
 * tags. We attach the JSON-LD to document.body — outside React 19's
 * head-reconciliation scope. Googlebot reads JSON-LD from anywhere in the
 * document so this remains 100% valid for SEO.
 */
export const usePageJsonLd = (data) => {
  const serialized = data ? JSON.stringify(data) : null;
  useEffect(() => {
    const tagId = "page-json-ld";
    let el = document.getElementById(tagId);
    if (!serialized) {
      if (el) el.remove();
      return;
    }
    if (!el || !el.isConnected) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = tagId;
      el.setAttribute("data-testid", "page-json-ld");
      document.body.appendChild(el);
    }
    if (el.textContent !== serialized) el.textContent = serialized;
  }, [serialized]);
};

/**
 * Sets document.title, canonical link and meta description for the current
 * SPA route. Lightweight alternative to react-helmet.
 *
 * @param {object} opts
 * @param {string} opts.title  Full page title (< 60 chars ideal)
 * @param {string} opts.path   Path e.g. "/about" or "/blog/abc"
 * @param {string} [opts.description]  Meta description (< 160 chars ideal)
 * @param {boolean} [opts.noindex]  If true, emits <meta name="robots" content="noindex, follow">
 *   so search engines drop the page from the index but still follow internal
 *   links. Used for thin variants of location / doorway-risk pages.
 * @param {Array<{hreflang: string, href: string}>} [opts.alternates]  hreflang
 *   alternate URLs (managed under `data-managed="alternate"` so re-renders
 *   replace the previous set cleanly).
 */
export const usePageMeta = ({ title, path, description, alternates, noindex }) => {
  useEffect(() => {
    if (title) document.title = title;

    const canonical = `${SITE}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    const desc = description || DEFAULT_DESCRIPTION;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    // robots meta — controls indexation. We manage a dedicated tag so
    // pages can flip between the default (index,follow) and noindex without
    // stale tags leaking between routes.
    let robots = document.querySelector('meta[name="robots"][data-managed="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      robots.setAttribute("data-managed", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");

    // hreflang alternates.
    // Clear the previous managed set first so navigating between pages
    // doesn't leave stale tags in the head.
    document
      .querySelectorAll('link[rel="alternate"][data-managed="alternate"]')
      .forEach((n) => n.remove());

    // Automatic bilingual alternates — every page implicitly has an EN and
    // a HI version served by the same URL with `?lang=hi`.  Google needs
    // an explicit hreflang set on both variants so it doesn't treat them
    // as duplicates; we always emit en-IN, hi-IN and x-default.
    const effectivePath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const autoAlternates = [
      { hreflang: "en-IN", href: `${SITE}${effectivePath}` },
      { hreflang: "hi-IN", href: `${SITE}${effectivePath}?lang=hi` },
      { hreflang: "x-default", href: `${SITE}${effectivePath}` },
    ];
    const explicit = Array.isArray(alternates) ? alternates : [];
    // Explicit entries win over auto ones for the same hreflang key.
    const byLang = new Map(autoAlternates.map((a) => [a.hreflang, a]));
    explicit.forEach((a) => { if (a?.hreflang) byLang.set(a.hreflang, a); });
    Array.from(byLang.values()).forEach(({ hreflang, href }) => {
      if (!hreflang || !href) return;
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", href.startsWith("http") ? href : `${SITE}${href}`);
      el.setAttribute("data-managed", "alternate");
      document.head.appendChild(el);
    });
  }, [title, path, description, JSON.stringify(alternates), noindex]);
};

export default usePageMeta;
